import React from "react";
import { Message } from "components/Atoms/Message";
import { Button } from "semantic-ui-react";
import { Modal } from "../../components";
import { GetClientAccessType, GetClientAccessTypeLabel } from "./ClientAccessUtils";
import { findEnumLabel } from "utilities/enumutils";
import { reloadOnSuccess } from "Client/utils";

interface Props {
  activeSubscription: server.dto.Subscription
  client: server.dto.Client
  isOpen: boolean
  onClose: () => void
}

const SuspendAccessContent = ({ clientId }: { clientId: number }) => {
  const onSuspendForNotPayingClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    const res = await window.CoreApi.Client.UpdateClientAccess({ clientId, access: "suspendedForNotPaying" })
    reloadOnSuccess(res);
  };

  const onSuspendForAbuseClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    const res = await window.CoreApi.Client.UpdateClientAccess({ clientId, access: "suspendedForAbuse" })
    reloadOnSuccess(res);
  };

  return <>
    <Message type="info" className="margin-bottom-large">
      <p>You can suspend a client so users can no longer access their data in SystemOne.</p>
      <p>This can be done for active subscriptions (i.e. trial, paid, free, read-only).</p>
      <p>Once the subscription changes to a closed state (i.e. cancelled, trial ended, paused) the suspension ends automatically.</p>
    </Message>
    <div className="margin-bottom-small">
      <Button color="red" type="button" onClick={onSuspendForNotPayingClick} fluid>Suspend for not paying</Button>
    </div>
    <div>
      <Button color="red" type="button" onClick={onSuspendForAbuseClick} fluid>Suspend for abuse</Button>
    </div>
  </>;
};

const GiveTemporaryAccessContent = ({ clientId }: { clientId: number }) => {
  const onClick = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    const res = await window.CoreApi.Client.UpdateClientAccess({ clientId, access: "temporarilyOpened" });
    reloadOnSuccess(res);
  };

  return <>
    <div className="margin-bottom-larger grow1">
      <Message type="info">
        <p>When subscription is in a closed state (i.e. cancelled, trial ended, paused) you can give temporary access, so a user can quickly do or look up something.</p>
        <p>The account closes automatically after 3 days.</p>
      </Message>
    </div>
    <div>
      <Button color="red" type="button" onClick={onClick} fluid>Give temporary access</Button>
    </div>
  </>;
};

const EndSuspensionContent = ({ clientId, activeSubscription }: { clientId: number, activeSubscription: server.dto.Subscription }) => {
  const onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    window.CoreApi.Client.ResetClientAccess({ clientId })
      .finally(() => {
        window.document.location.href = document.location.href;
      });
  };

  return <>
    <div className="margin-bottom-larger grow1">
      <Message type="info">
        <p>End the suspension so the user can continue his {findEnumLabel(activeSubscription.type, serverReference.subscriptionTypes)} subscription.</p>
      </Message>
    </div>

    <div>
      <Button color="red" type="button" onClick={onClick} fluid>End account suspension</Button>
    </div>
  </>;
};

const EndTemporaryAccessContent = ({ clientId }: { clientId: number }) => {

  const onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    window.CoreApi.Client.ResetClientAccess({ clientId })
      .finally(() => {
        window.document.location.href = document.location.href;
      });
  };

  return <>
    <Button color="red" type="button" onClick={onClick} fluid>End temporary access</Button>
  </>;
};

const ChangeClientAccessContent = (props: { client: server.dto.Client, activeSubscription: server.dto.Subscription }) => {
  const accessType = GetClientAccessType(props.client.access);

  switch (accessType) {
    case "active":
      return <SuspendAccessContent clientId={props.client.clientId} />;
    case "closed":
      return <GiveTemporaryAccessContent clientId={props.client.clientId} />;
    case "suspended":
      return <EndSuspensionContent clientId={props.client.clientId} activeSubscription={props.activeSubscription} />;
    case "temporarilyOpened":
      return <EndTemporaryAccessContent clientId={props.client.clientId} />;
  }
};

const ChangeClientAccessModal = (props: Props) => {
  const accessType = GetClientAccessType(props.client.access);
  const label = GetClientAccessTypeLabel(accessType);

  return <Modal
    width="mini"
    title={label}
    handleClose={props.onClose}
    isOpen={props.isOpen}>
    <ChangeClientAccessContent client={props.client} activeSubscription={props.activeSubscription} />
  </Modal>;
};

export default ChangeClientAccessModal;